import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Dashboard as DashboardIcon,
  BugReport as BugIcon,
  Assessment as SprintIcon,
  GitHub as RepoIcon,
  Security as SecurityIcon,
  Speed as PerformanceIcon,
  LocalOffer as ReleaseIcon,
  Science as TestsIcon,
  AutoAwesome as AutomationIcon,
  Checklist as PrIcon,
  Computer as InstanceIcon,
  Engineering as EngineeringIcon,
  HealthAndSafety as HealthIcon,
  MonitorHeart as OpsIcon,
  Gavel as ComplianceIcon,
  CloudUpload as DeploymentIcon,
  Assignment as TsiIcon,
  Description as WSRIcon
} from '@mui/icons-material';

/**
 * Executive Summary – Application overview for the public Metrics Portal.
 * Covers capabilities, data sources (no secrets), and tech stack.
 */
const ExecutiveSummary = () => {
  const darkGreen = '#1D4434';

  const capabilities = [
    {
      id: 'health',
      label: 'Health Dashboard',
      icon: <HealthIcon />,
      desc: 'Provides a single pane for QA health metrics, team status, and key indicators. Its importance lies in giving stakeholders an at-a-glance view of delivery and quality health so decisions can be made quickly without digging into multiple tools.'
    },
    {
      id: 'delivery',
      label: 'Delivery / Planning',
      icon: <DashboardIcon />,
      desc: 'Offers Epic Gantt charts, work items (Epics, Features, PBIs, Bugs, Tasks), and release versions for milestones. It is important because it aligns planning visibility across engineering and QA, tracks progress toward release dates, and surfaces dependencies and bottlenecks in one place.'
    },
    {
      id: 'sprint',
      label: 'Sprint Reports',
      icon: <SprintIcon />,
      desc: 'Delivers sprint-level reporting, velocity, and team allocation views. It matters for sprint retrospectives, capacity planning, and ensuring commitments are visible and measurable across teams.'
    },
    {
      id: 'repos',
      label: 'Repositories',
      icon: <RepoIcon />,
      desc: 'Lists repositories by stream with Snyk vulnerability counts and sync status. It is important for linking code assets to quality and security metrics and for tracking which codebases are covered by security scanning.'
    },
    {
      id: 'bugs',
      label: 'Production Bugs',
      icon: <BugIcon />,
      desc: 'Tracks production defects, trends, and age so teams can prioritize fixes and measure quality over time. It is critical for reducing escape rate, meeting SLAs, and feeding data into release and quality decisions.'
    },
    {
      id: 'eng',
      label: 'Engineering Processes',
      icon: <EngineeringIcon />,
      desc: 'Surfaces RCA metrics, technical debt, and engineering process data tied to work items. It helps teams focus improvement efforts, track technical debt payoff, and align engineering health with business outcomes.'
    },
    {
      id: 'releases',
      label: 'Releases',
      icon: <ReleaseIcon />,
      desc: 'Supports release comparison and version tracking across builds and environments. It is important for release governance, understanding what shipped when, and coordinating QA sign-off with release timelines.'
    },
    {
      id: 'wsr',
      label: 'WSR',
      icon: <WSRIcon />,
      desc: 'Enables weekly status reports with auto-population from work items and report generation. It reduces manual report prep and keeps leadership and stakeholders aligned on progress and risks.'
    },
    {
      id: 'tests',
      label: 'Tests',
      icon: <TestsIcon />,
      desc: 'Shows test cases, execution results, and coverage in one place. It is important for test traceability, understanding what is tested per release, and identifying gaps before production.'
    },
    {
      id: 'automation',
      label: 'Automation',
      icon: <AutomationIcon />,
      desc: 'Tracks test automation metrics, coverage, and trends over time. It helps teams invest in automation where it matters most and demonstrates ROI of automated testing.'
    },
    {
      id: 'pr',
      label: 'Pull Requests',
      icon: <PrIcon />,
      desc: 'Surfaces recommended tests and PR-impacted methods via Drill4j so developers run the right tests per change. It is important for faster, safer releases and reducing flaky or redundant test runs.'
    },
    {
      id: 'plans',
      label: 'Test Plans',
      icon: <TestsIcon />,
      desc: 'Organizes test plans and scenarios linked to work items and releases. It ensures test planning is visible, traceable, and aligned with delivery so nothing is missed at release time.'
    },
    {
      id: 'instances',
      label: 'Test Instances',
      icon: <InstanceIcon />,
      desc: 'Manages test instances, environments, and service metadata (e.g. CBV). It is important for knowing which environments are available, their status, and how they map to applications under test.'
    },
    {
      id: 'perf',
      label: 'Performance',
      icon: <PerformanceIcon />,
      desc: 'Stores and visualizes performance test executions, trends, and reports. It is critical for catching regressions early, meeting non-functional requirements, and tracking performance over releases.'
    },
    {
      id: 'snyk',
      label: 'SNYK',
      icon: <SecurityIcon />,
      desc: 'Provides a security vulnerability dashboard with team and project summaries from Snyk data. It is important for prioritizing fixes, meeting security compliance, and reducing risk from known vulnerabilities.'
    },
    {
      id: 'a11y',
      label: 'Accessibility',
      icon: <ComplianceIcon />,
      desc: 'Tracks accessibility compliance and issues by team or product. It helps meet accessibility standards, prioritize remediation, and demonstrate progress to auditors and users.'
    },
    {
      id: 'deploy',
      label: 'Deployment',
      icon: <DeploymentIcon />,
      desc: 'Shows deployment status and history across environments. It is important for release coordination, rollback decisions, and understanding what is live in each environment.'
    },
    {
      id: 'tsi',
      label: 'TSI Dashboard',
      icon: <TsiIcon />,
      desc: 'Aggregates TSI (Technical Support / Implementation) ticket and support metrics. It helps support and engineering see demand, resolution trends, and focus areas for product and docs.'
    },
    {
      id: 'ops',
      label: 'Major Incidents',
      icon: <OpsIcon />,
      desc: 'Integrates ServiceNow incidents and alerts with MTTA/MTTR and top-monitor views. It is critical for incident response, reliability improvement, and aligning ops with business impact.'
    }
  ];

  const dataSources = [
    { name: 'Azure DevOps (ADO)', purpose: 'Work items (Epics, Features, PBIs, Bugs, Tasks), test cases, queries (WIQL). Full/delta import. No credentials shared.' },
    { name: 'MongoDB', purpose: 'Primary data store for work items, tests, repos, sprint data, WSR, config, and app state.' },
    { name: 'Snyk', purpose: 'Security vulnerability data via CSV upload or sync-from-URL. Team/project mapping only; no API keys in UI.' },
    { name: 'ServiceNow', purpose: 'Incidents, alerts, and ticket data for Ops Center. Configured via admin; no secrets exposed.' },
    { name: 'Datadog', purpose: 'Optional monitoring integration for deployment/health. Configuration stored server-side only.' },
    { name: 'GitHub', purpose: 'Repository metadata and sync for the Repositories view. Uses configured integration; no tokens in summary.' },
    { name: 'Drill4j', purpose: 'Test impact and recommended tests (pull requests, impacted methods).' },
    { name: 'Instance Management / CBV', purpose: 'Test instance and service data (e.g. CBV JSON). Upload/import; no credentials listed.' },
    { name: 'RCA / Technical Debt', purpose: 'Engineering process data sourced from ADO and internal RCA workflows.' },
    { name: 'Sprint & Timeline Config', purpose: 'Sprint names, timeline configuration, and team-stream data stored in MongoDB.' }
  ];

  const techStack = [
    { category: 'Frontend', items: ['React 18', 'React Router 6', 'Material UI (MUI) 5', 'Emotion (styling)', 'Chart.js & react-chartjs-2', 'Recharts', 'Axios', 'date-fns / Day.js', 'react-markdown & remark-gfm', 'jsPDF & html2canvas (reports)', 'React Dropzone', 'MUI X Date Pickers'] },
    { category: 'Backend', items: ['Node.js (>=18)', 'Express 4', 'Mongoose (MongoDB ODM)', 'JWT (jsonwebtoken)', 'bcryptjs', 'cookie-parser', 'cors', 'morgan', 'multer (uploads)', 'node-cron', 'azure-devops-node-api', 'Puppeteer (optional)', 'pdfkit', 'xlsx', 'html-to-text', 'joi (validation)', 'swagger-jsdoc & swagger-ui-express'] },
    { category: 'Database', items: ['MongoDB 7', 'Mongoose schemas and models'] },
    { category: 'Infrastructure & DevOps', items: ['Docker / Podman', 'Containerfile / Dockerfile', 'dotenv for configuration'] },
    { category: 'Build & Tooling', items: ['Create React App (react-scripts)', 'concurrently (dev)', 'nodemon (server dev)'] }
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', py: 4, px: { xs: 2, sm: 3, md: 4 }, fontSize: 18 }}>
      <Button
        component={RouterLink}
        to="/"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, color: darkGreen, fontSize: 18 }}
      >
        Back to Portal
      </Button>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: darkGreen, fontWeight: 600, fontSize: '2.25rem' }}>
        HighQ Metrics Portal
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: 18 }}>
        Executive overview of the HighQ Metrics Portal: capabilities, data sources, and technology stack.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'flex-start'
        }}
      >
        {/* Left 60% – Capabilities */}
        <Box sx={{ width: { xs: '100%', md: 'calc(60% - 12px)' }, flexShrink: 0, minWidth: 0 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ color: darkGreen, fontWeight: 600, fontSize: '1.5rem' }}>
            Executive Summary – Capabilities
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 16 }}>
            The Metrics Portal provides a single place for development and QA metrics, planning, and compliance. All items below are available on the public dashboard (read-only) where applicable.
          </Typography>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <List disablePadding>
              {capabilities.map((cap) => (
                <ListItem key={cap.id} alignItems="flex-start" sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
                  <ListItemIcon sx={{ minWidth: 44, color: darkGreen, mt: 0.5 }}>{cap.icon}</ListItemIcon>
                  <ListItemText
                    primary={cap.label}
                    secondary={cap.desc}
                    primaryTypographyProps={{ fontWeight: 600, variant: 'h6', sx: { mb: 0.5, fontSize: '1.125rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.secondary', component: 'p', sx: { fontSize: 18 } }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Right 40% – Data Sources + Tech Stack */}
        <Box sx={{ width: { xs: '100%', md: 'calc(40% - 12px)' }, flexShrink: 0, minWidth: 0 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ color: darkGreen, fontWeight: 600, fontSize: '1.5rem' }}>
            Data Sources
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: 16 }}>
            Sources used to sync data. Config is managed in admin; no secrets disclosed.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <List disablePadding dense>
              {dataSources.map((src, idx) => (
                <ListItem key={idx} sx={{ py: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                  <ListItemText
                    primary={src.name}
                    secondary={src.purpose}
                    primaryTypographyProps={{ fontWeight: 600, variant: 'body2', sx: { fontSize: 16 } }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary', sx: { fontSize: 14 } }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Typography variant="h5" component="h2" gutterBottom sx={{ color: darkGreen, fontWeight: 600, mt: 2, fontSize: '1.5rem' }}>
            Tech Stack
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: 16 }}>
            Technologies used across frontend, backend, database, and infrastructure.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {techStack.map((group, idx) => (
              <Box key={idx} sx={{ mb: idx < techStack.length - 1 ? 2 : 0 }}>
                <Typography variant="subtitle2" sx={{ color: darkGreen, fontWeight: 600, mb: 0.5, fontSize: 14 }}>
                  {group.category}
                </Typography>
                <List dense disablePadding sx={{ pl: 2 }}>
                  {group.items.map((item, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0 }}>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'caption', sx: { fontSize: 14 } }} />
                    </ListItem>
                  ))}
                </List>
                {idx < techStack.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ExecutiveSummary;
